/**
 * Google Cloud Text-to-Speech (Chirp 3 HD voices).
 *
 * Used by the public-transcription page to read aloud clinical narratives
 * produced by the gateway's /api/scribe/clinical-narrative endpoint
 * (which calls a Gemini-backed n8n workflow). Replaced the earlier
 * Deepgram Aura WebSocket implementation — the team standardised on
 * Google Chirp HD for both narrative voice quality and to keep all
 * GenAI calls on a single provider.
 *
 * Why REST (not streaming):
 *   Google's text-to-speech streaming API is gRPC-only and requires
 *   OAuth2 — neither works from a browser without a backend proxy. The
 *   REST `text:synthesize` endpoint accepts an API key, returns full
 *   audio in one response, and is more than fast enough for the
 *   narratives we send (typically 200-800 chars, ~1-2s synthesis).
 *
 *   For multi-paragraph narratives we split on sentence boundaries and
 *   issue parallel REST calls so the user hears the first sentence
 *   while later ones are still being synthesised — keeps perceived
 *   latency comparable to a true streaming TTS.
 */
import { envConfig } from './env-config';

const TTS_ENDPOINT = 'https://texttospeech.googleapis.com/v1/text:synthesize';
const DEFAULT_VOICE = 'en-US-Chirp3-HD-Kore';
const DEFAULT_LANGUAGE_CODE = 'en-US';
// Each REST call is capped server-side at 5000 chars for Chirp HD; we
// stay well under so a single sentence never blows the limit.
const CHIRP_MAX_CHARS = 1500;

export interface SpeakOptions {
  /** Chirp HD voice name. Defaults to en-US-Chirp3-HD-Kore. */
  model?: string;
  /** Language code, e.g. 'en-US'. */
  languageCode?: string;
  /** Volume 0..1. Defaults to 0.9. */
  volume?: number;
  /**
   * Called the first time audio begins playing (after the first chunk's
   * MP3 is downloaded and started). Useful for switching the UI from a
   * "Generating…" placeholder into the actual speaking state.
   */
  onAudioStart?: () => void;
  /** Called when playback completes naturally OR after stop() is invoked. */
  onComplete?: (reason: 'finished' | 'stopped' | 'error') => void;
  /** Called on the first hard error. The handle settles after. */
  onError?: (err: Error) => void;
}

export interface SpeakHandle {
  /** Stop playback immediately. Safe to call multiple times. */
  stop: () => void;
  /** Resolves when playback ends for any reason. */
  done: Promise<void>;
}

/**
 * Speak `text` aloud through the user's default audio output, using
 * Google Cloud Chirp HD via REST. Returns a handle that lets the caller
 * abort playback. The `done` promise settles when audio ends — natural
 * finish, stop(), or error.
 *
 * Chunking strategy: split into sentences, kick off all the REST calls
 * in parallel, then play the responses in order. Each subsequent chunk
 * starts loading while the prior one plays, so total wall-clock time
 * ≈ synthesis time for the longest single chunk + sum of playback.
 */
export function speakText(text: string, options: SpeakOptions = {}): SpeakHandle {
  const apiKey = envConfig.googleTts.apiKey;
  const voice = options.model || DEFAULT_VOICE;
  const languageCode = options.languageCode || DEFAULT_LANGUAGE_CODE;
  const volume = typeof options.volume === 'number' ? options.volume : 0.9;

  let stopped = false;
  let currentAudio: HTMLAudioElement | null = null;
  const objectUrls: string[] = [];

  const releaseObjectUrls = () => {
    while (objectUrls.length > 0) {
      const u = objectUrls.pop();
      if (u) {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* noop */
        }
      }
    }
  };

  const cleanup = () => {
    if (currentAudio) {
      try {
        currentAudio.pause();
        currentAudio.src = '';
      } catch {
        /* noop */
      }
      currentAudio = null;
    }
    releaseObjectUrls();
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    cleanup();
  };

  const done = (async (): Promise<void> => {
    if (!apiKey) {
      const err = new Error(
        'Google TTS API key not configured (VITE_GOOGLE_TTS_API_KEY)',
      );
      options.onError?.(err);
      options.onComplete?.('error');
      throw err;
    }
    const cleaned = (text || '').trim();
    if (!cleaned) {
      options.onComplete?.('finished');
      return;
    }

    const chunks = chunkBySentence(cleaned, CHIRP_MAX_CHARS);

    // Kick off all synthesise calls IN PARALLEL. Each promise resolves
    // to an MP3 Blob. Errors are caught and propagated to the caller
    // via the loop below.
    const pendingBlobs: Promise<Blob>[] = chunks.map((chunk) =>
      synthesiseChunk(chunk, apiKey, voice, languageCode),
    );

    let exitReason: 'finished' | 'stopped' | 'error' = 'finished';
    let firstAudible = false;

    for (let i = 0; i < pendingBlobs.length; i++) {
      if (stopped) {
        exitReason = 'stopped';
        break;
      }
      let blob: Blob;
      try {
        blob = await pendingBlobs[i];
      } catch (err) {
        if (stopped) {
          exitReason = 'stopped';
          break;
        }
        exitReason = 'error';
        const e = err instanceof Error ? err : new Error(String(err));
        options.onError?.(e);
        cleanup();
        options.onComplete?.('error');
        throw e;
      }
      if (stopped) {
        exitReason = 'stopped';
        break;
      }
      const url = URL.createObjectURL(blob);
      objectUrls.push(url);
      const audio = new Audio(url);
      audio.volume = volume;
      currentAudio = audio;
      try {
        await playOnce(audio, () => stopped);
      } catch (err) {
        if (stopped) {
          exitReason = 'stopped';
          break;
        }
        exitReason = 'error';
        const e = err instanceof Error ? err : new Error(String(err));
        options.onError?.(e);
        cleanup();
        options.onComplete?.('error');
        throw e;
      }
      if (!firstAudible) {
        firstAudible = true;
        try {
          options.onAudioStart?.();
        } catch {
          /* noop */
        }
      }
      currentAudio = null;
    }

    cleanup();
    options.onComplete?.(exitReason);
  })();

  // Don't propagate the rejection — caller can opt in via handle.done.
  done.catch(() => {
    /* error already surfaced via onError */
  });

  return { stop, done };
}

/**
 * One Google TTS REST call for a single chunk. Returns the MP3 Blob.
 */
async function synthesiseChunk(
  text: string,
  apiKey: string,
  voice: string,
  languageCode: string,
): Promise<Blob> {
  const response = await fetch(`${TTS_ENDPOINT}?key=${encodeURIComponent(apiKey)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      input: { text },
      voice: { languageCode, name: voice },
      audioConfig: {
        audioEncoding: 'MP3',
        speakingRate: 1.0,
      },
    }),
  });
  if (!response.ok) {
    const detail = await safeReadError(response);
    throw new Error(
      `Google TTS ${response.status}${detail ? `: ${detail}` : ''}`,
    );
  }
  const data = await response.json();
  const audioBase64: string | undefined = data?.audioContent;
  if (!audioBase64) {
    throw new Error('Google TTS response missing audioContent');
  }
  return base64ToBlob(audioBase64, 'audio/mpeg');
}

/**
 * Play one HTMLAudioElement to its end. Resolves on ended, rejects on
 * error. Polls `isStopped` so an outside stop() call short-circuits
 * the await (otherwise we'd wait for the audio's natural ended event).
 */
function playOnce(
  audio: HTMLAudioElement,
  isStopped: () => boolean,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const onEnded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Audio playback error'));
    };
    const cleanup = () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.play().catch((err) => {
      cleanup();
      reject(err);
    });
    // Stop poll — checks every 80ms whether the caller has requested
    // abort. Light enough overhead and avoids needing the stop logic
    // to plumb an event into here.
    const tick = window.setInterval(() => {
      if (isStopped()) {
        window.clearInterval(tick);
        try {
          audio.pause();
        } catch {
          /* noop */
        }
        cleanup();
        resolve();
      }
    }, 80);
    audio.addEventListener('ended', () => window.clearInterval(tick), {
      once: true,
    });
    audio.addEventListener('error', () => window.clearInterval(tick), {
      once: true,
    });
  });
}

function base64ToBlob(b64: string, mimeType: string): Blob {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

async function safeReadError(response: Response): Promise<string> {
  try {
    const text = await response.text();
    return text.slice(0, 240);
  } catch {
    return '';
  }
}

/**
 * Split text into chunks at sentence boundaries, keeping each chunk
 * under maxChars. Hard-wraps a single oversized sentence as a last
 * resort.
 */
function chunkBySentence(text: string, maxChars: number): string[] {
  if (text.length <= maxChars) return [text];
  const sentences = text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const out: string[] = [];
  let buf = '';
  for (const sentence of sentences) {
    if (sentence.length > maxChars) {
      if (buf) {
        out.push(buf);
        buf = '';
      }
      for (let i = 0; i < sentence.length; i += maxChars) {
        out.push(sentence.slice(i, i + maxChars));
      }
      continue;
    }
    if (buf.length + sentence.length + 1 > maxChars) {
      out.push(buf);
      buf = sentence;
    } else {
      buf = buf ? `${buf} ${sentence}` : sentence;
    }
  }
  if (buf) out.push(buf);
  return out;
}
